import React, { useEffect, useState } from 'react';
import MainLayout from '../../components/layouts/MainLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import { useToast } from '../../components/common/Toast';
import { adminService } from '../../services/admin.service';
import { theme } from '../../config/theme';

const Subscriptions = () => {
  const toast = useToast();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [tierFilter, setTierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;

  useEffect(() => {
    fetchSubscriptions();
  }, [page, tierFilter, statusFilter]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await adminService.getSubscriptions(
        page, 
        limit, 
        tierFilter || null, 
        statusFilter || null
      );
      setSubscriptions(response.data?.subscriptions || []);
      setTotalPages(response.data?.pagination?.totalPages || 1);
    } catch (error) {
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'yearly': return theme.colors.success;
      case 'monthly': return theme.colors.primary.main;
      default: return theme.colors.gray[600];
    }
  };

  const tableStyles = {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: theme.spacing.lg,
    backgroundColor: theme.colors.white,
    boxShadow: theme.shadows.sm,
    borderRadius: theme.borderRadius.md,
    overflow: 'hidden'
  };

  const thStyles = {
    padding: theme.spacing.md,
    textAlign: 'left',
    backgroundColor: theme.colors.gray[50],
    color: theme.colors.text.secondary,
    fontWeight: theme.typography.fontWeight.semibold,
    borderBottom: `1px solid ${theme.colors.gray[200]}`,
    textTransform: 'uppercase',
    fontSize: theme.typography.fontSize.xs,
  };

  const tdStyles = {
    padding: theme.spacing.md,
    borderBottom: `1px solid ${theme.colors.gray[100]}`,
    color: theme.colors.text.primary,
    fontSize: theme.typography.fontSize.sm,
  };

  const filterSelectStyles = {
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.colors.gray[300]}`,
    backgroundColor: theme.colors.white,
    color: theme.colors.text.primary,
    fontSize: theme.typography.fontSize.sm,
    minWidth: '150px',
  };

  return (
    <MainLayout>
      <div style={{ padding: theme.spacing.xl }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing.xl, flexWrap: 'wrap', gap: theme.spacing.md }}>
          <h1 style={{ fontSize: theme.typography.fontSize['3xl'], fontWeight: theme.typography.fontWeight.bold }}>
            Seller Subscriptions
          </h1>
          
          <div style={{ display: 'flex', gap: theme.spacing.md }}>
            <select 
              style={filterSelectStyles}
              value={tierFilter}
              onChange={(e) => { setTierFilter(e.target.value); setPage(1); }}
            >
              <option value="">All Tiers</option>
              <option value="free">Free</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>

            <select 
              style={filterSelectStyles}
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {loading && subscriptions.length === 0 ? (
          <LoadingSpinner fullScreen={false} />
        ) : subscriptions.length === 0 ? (
          <EmptyState title="No subscriptions found" />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyles}>
              <thead>
                <tr>
                  <th style={thStyles}>Seller</th>
                  <th style={thStyles}>Owner Name</th>
                  <th style={thStyles}>Email</th>
                  <th style={thStyles}>Tier</th>
                  <th style={thStyles}>Status</th>
                  <th style={thStyles}>Amount</th>
                  <th style={thStyles}>End Date</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map(sub => (
                  <tr key={sub.id} style={{ transition: theme.transitions.base, ':hover': { backgroundColor: theme.colors.gray[50] } }}>
                    <td style={tdStyles}>
                      <div style={{ fontWeight: theme.typography.fontWeight.medium }}>
                        {sub.seller?.store_name || 'N/A'}
                      </div>
                    </td>
                    <td style={tdStyles}>
                      {sub.seller?.user?.first_name} {sub.seller?.user?.last_name}
                    </td>
                    <td style={tdStyles}>{sub.seller?.user?.email}</td>
                    <td style={tdStyles}>
                      <span style={{ 
                        display: 'inline-block',
                        padding: '4px 8px', 
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        backgroundColor: `${getTierColor(sub.tier)}15`,
                        color: getTierColor(sub.tier),
                        textTransform: 'capitalize'
                      }}>
                        {sub.tier}
                      </span>
                    </td>
                    <td style={tdStyles}>
                      <span style={{ textTransform: 'capitalize' }}>{sub.status}</span>
                    </td>
                    <td style={tdStyles}>
                      ₹{sub.amount}
                    </td>
                    <td style={tdStyles}>
                      {sub.end_date ? new Date(sub.end_date).toLocaleDateString() : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: theme.spacing.xl, gap: theme.spacing.sm }}>
                <button 
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                  style={{
                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                    border: `1px solid ${theme.colors.gray[300]}`,
                    borderRadius: theme.borderRadius.md,
                    background: page === 1 ? theme.colors.gray[100] : theme.colors.white,
                    cursor: page === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Previous
                </button>
                <span style={{ padding: `${theme.spacing.sm} ${theme.spacing.md}` }}>
                  Page {page} of {totalPages}
                </span>
                <button 
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                  style={{
                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                    border: `1px solid ${theme.colors.gray[300]}`,
                    borderRadius: theme.borderRadius.md,
                    background: page === totalPages ? theme.colors.gray[100] : theme.colors.white,
                    cursor: page === totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default Subscriptions;
